import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';

import { useEmployeeTimeline } from '../../../hooks/useEmployees';

const EVENT_LABELS = {
  PROMOTION: 'Promotion',
  TRANSFER: 'Internal transfer',
  CONFIRMATION: 'Confirmation',
  RESIGNATION: 'Resignation',
  STATUS_CHANGE: 'Status change',
  SALARY_REVISION: 'Salary revision',
};

const FIELD_LABELS = {
  department: 'Department',
  designation: 'Designation',
  branch: 'Branch',
  reporting_manager: 'Reporting manager',
  grade: 'Grade',
  employment_status: 'Employment status',
  status: 'Record status',
  confirmation_date: 'Confirmation date',
  resigned: 'Resigned',
  resignation_date: 'Resignation date',
  salary_amount: 'Salary amount',
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return 'Not set';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return value.name || value.label || 'Not set';
  return String(value);
};

const changedFields = (event) => {
  const before = event.previous_state || {};
  const after = event.new_state || {};
  return Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
    .filter((field) => JSON.stringify(before[field]) !== JSON.stringify(after[field]))
    .map((field) => ({
      field,
      before: displayValue(before[field]),
      after: displayValue(after[field]),
    }));
};

const formatDate = (value) => {
  if (!value) return 'Date unavailable';
  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? String(value)
    : parsed.toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
      });
};

const ProfileTimelineTab = ({ employeeId }) => {
  const { data, isLoading, isError, refetch, isFetching } = useEmployeeTimeline(employeeId);

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={1.5} sx={{ py: 8 }}>
        <CircularProgress size={34} />
        <Typography color="text.secondary">Loading employee history...</Typography>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        action={(
          <Button color="inherit" size="small" onClick={() => refetch()} disabled={isFetching}>
            Retry
          </Button>
        )}
      >
        Employee history could not be loaded.
      </Alert>
    );
  }

  const events = data?.results || [];
  if (events.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
        <HistoryIcon sx={{ fontSize: 46, mb: 1, color: '#94a3b8' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155' }}>
          No lifecycle history yet
        </Typography>
        <Typography variant="body2">
          Promotions, transfers, status changes and salary revisions will appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2} aria-label="Employee lifecycle timeline">
      {events.map((event) => {
        const changes = changedFields(event);
        return (
          <Box
            key={event.id}
            sx={{
              p: { xs: 2, sm: 2.5 },
              border: '1px solid #e8edf5',
              borderRadius: '18px',
              bgcolor: '#fff',
              boxShadow: '0 5px 18px rgba(15, 23, 42, 0.05)',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
              sx={{ mb: changes.length ? 2 : 0 }}
            >
              <Chip
                label={EVENT_LABELS[event.event_type] || event.event_type}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
              <Typography variant="body2" color="text.secondary">
                {formatDate(event.effective_date)}
              </Typography>
            </Stack>

            {changes.map(({ field, before, after }) => (
              <Box
                key={field}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '170px 1fr' },
                  gap: { xs: 0.5, sm: 2 },
                  py: 1,
                  borderTop: '1px solid #f1f5f9',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#475569' }}>
                  {FIELD_LABELS[field] || field}
                </Typography>
                <Typography variant="body2" sx={{ color: '#334155' }}>
                  <Box component="span" sx={{ color: '#94a3b8' }}>{before}</Box>
                  {' → '}
                  <Box component="span" sx={{ fontWeight: 700 }}>{after}</Box>
                </Typography>
              </Box>
            ))}

            {event.remarks && (
              <Typography variant="body2" sx={{ mt: 1.5, color: '#64748b' }}>
                {event.remarks}
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default ProfileTimelineTab;
